import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge, Spinner } from "../../../components/ui";
import { useCerrarCaso } from "../../../features/centinela/mutations";
import {
  useCentinelaCaseMedia,
  useCentinelaConfig,
  useCentinelaUnitCalls,
  useCentinelaUnitEvents,
} from "../../../features/centinela/queries";
import type { MonitoringUnitRow } from "../hooks/useMonitoringCenter";
import { formatAgo } from "../services/formatWatchTime";
import { lapso } from "../services/lapso";
import { mapCentinelaCalls } from "../services/mapCentinelaCall";
import type { CentinelaCaseView } from "../services/mapCentinelaCase";
import { mapCentinelaConfig } from "../services/mapCentinelaConfig";
import {
  CASE_STEP_CHANNEL_ICONS,
  CASE_STEP_STATUS_TONES,
  CASE_URGENCY_TONES,
  WATCH_STATUS_MAP_COLORS,
} from "../theme";
import LastCallAudio from "./LastCallAudio";
import CaseDashcamShots from "./CaseDashcamShots";
import CaseLadder from "./CaseLadder";

const VISIBLE_STEPS = 4;

/**
 * La tarjeta de un CASO: qué pasó, cómo se atendió y con qué evidencia.
 *
 * ── Por qué no vive dentro de la tarjeta de la unidad ───────────────────────
 * Estaba ahí, colgando debajo de la ficha, y eran dos tarjetas apiladas en una.
 * Quien abría una unidad para saber quién la conduce se encontraba media
 * pantalla de un caso que quizá no venía a ver, y quien llegaba desde un rombo
 * del rastro tenía que pasar por la ficha para llegar a lo que buscaba.
 *
 * Son dos preguntas distintas —«¿quién va en esta unidad?» y «¿qué pasó en este
 * hecho?»— y ahora se turnan en la misma columna.
 *
 * ── De dónde se abre ────────────────────────────────────────────────────────
 * De un rombo del rastro del día, de la inteligencia en vivo, o del enlace de
 * «caso abierto» en la ficha de la unidad. Las tres llegan a lo mismo.
 */

function PanelPending({ label }: { label: string }) {
  return (
    <p className="flex items-center gap-2 border-t border-line pt-3 text-[10.5px] text-content-faint">
      <Spinner size="xs" className="text-primary-500" label={label} />
      {label}
    </p>
  );
}

export interface MonitoringCasePanelProps {
  caseView: CentinelaCaseView;
  /** La unidad del caso, para el encabezado y las llamadas. */
  row: MonitoringUnitRow | null;
  labelOfCase: (item: CentinelaCaseView) => string;
  onClose: () => void;
  /**
   * Abrir con el formulario de cierre ya desplegado.
   *
   * Lo usa el atajo «Resolver» de la tarjeta de la unidad. Los dos pasos
   * siguen existiendo —el botón y el campo— pero el primero ya se dio: quien
   * llegó por ese atajo venía a cerrar, y hacerle repetir la intención no lo
   * hace pensarlo más, sólo lo hace tocar dos veces.
   *
   * La nota sigue siendo obligatoria, que es la guarda que de verdad importa.
   */
  abrirEnCierre?: boolean;
}

export default function MonitoringCasePanel({
  caseView,
  row,
  labelOfCase,
  onClose,
  abrirEnCierre = false,
}: MonitoringCasePanelProps) {
  const { t, i18n } = useTranslation("common");

  const callsQuery = useCentinelaUnitCalls(row?.deviceId ?? null);
  const eventsQuery = useCentinelaUnitEvents(row?.deviceId ?? null);
  const configQuery = useCentinelaConfig();
  const mediaQuery = useCentinelaCaseMedia(caseView.id);

  const calls = useMemo(() => mapCentinelaCalls(callsQuery.data?.calls), [callsQuery.data]);
  const lastCall = calls[0] ?? null;

  const maxAttempts = useMemo(
    () => mapCentinelaConfig(configQuery.data).escalation.max_driver_attempts,
    [configQuery.data],
  );

  const steps = useMemo(() => {
    const match = (eventsQuery.data?.events ?? []).find((event) => event.case.id === caseView.id);
    return (match?.steps ?? []).slice(-VISIBLE_STEPS).reverse();
  }, [eventsQuery.data, caseView]);

  const tone = row ? WATCH_STATUS_MAP_COLORS[row.status] : "#94a3b8";

  /*
    ── Cerrar a mano ─────────────────────────────────────────────────────────
    Sólo aparece si el caso sigue abierto. La nota es obligatoria y el formulario
    se abre en dos pasos —botón, luego campo— a propósito: cerrar un caso es una
    afirmación con consecuencias, y un botón que cierra al primer clic invita a
    vaciar la lista sin leerla.
  */
  const cerrar = useCerrarCaso();
  const [cerrando, setCerrando] = useState(abrirEnCierre);
  const [nota, setNota] = useState("");

  const when = (iso: string) =>
    new Date(iso).toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-card">
      <div className="flex shrink-0 items-center gap-2 border-b border-line px-3 py-2">
        <Badge tone={CASE_URGENCY_TONES[caseView.urgency]} size="sm">
          {t(`monitoreoIa.caseEvents.urgency.${caseView.urgency}`)}
        </Badge>
        <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-content">
          {labelOfCase(caseView)}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("monitoreoIa.monitoringCenter.closePanel")}
          className="shrink-0 text-content-faint transition-colors hover:text-content"
        >
          <i className="ri-close-line text-sm" />
        </button>
      </div>

      {row ? (
        <p className="shrink-0 border-b border-line px-3 py-1.5 text-[10.5px] text-content-muted">
          {row.name}
          {row.driver ? ` · ${row.driver}` : ""}
        </p>
      ) : null}

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        {/*
          ── Qué pasó, dónde y por qué importa ────────────────────────────────
          El panel decía «Se quedé sin señal» y nada más. Con eso no se puede
          decidir: es un aviso, no un caso. Lo que hace falta para actuar son
          tres cosas y van juntas arriba del todo, antes que la escalera y los
          pasos —que cuentan qué hizo CENTINELA, no qué pasó—.
        */}
        <div className="rounded-lg border border-line bg-surface p-2.5">
          <p className="text-[12px] font-medium leading-snug text-content">
            {caseView.hecho ?? labelOfCase(caseView)}
          </p>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] max-sm:text-[11px] text-content-faint">
            {/*
              ── Dos duraciones distintas, y hay que distinguirlas ───────────
              Arriba, el hecho dice «lleva 15 minutos detenida»: eso es la
              UNIDAD. Aquí se habla del CASO: cuánto lleva sin resolverse o
              cuánto tardó en resolverse. Puestas juntas y sin etiqueta se
              leían como una contradicción —«lleva 15 minutos» y «duró 0 min»—
              cuando son dos relojes de cosas distintas.

              El «0 min» además tenía su propia trampa: un caso archivado al
              instante por no haber a quién llamar duraba cero de verdad, y ese
              cero no informaba de nada. Ahora se dice lo que pasó.
            */}
            <span className="flex items-center gap-1">
              <i className="ri-time-line" aria-hidden="true" />
              {caseView.duracionMin == null
                ? "—"
                : caseView.isOpen
                  ? t("monitoreoIa.caso.abiertoHace", {
                      defaultValue: "caso abierto hace {{lapso}}",
                      lapso: lapso(caseView.duracionMin, t),
                    })
                  : caseView.duracionMin < 1
                    ? t("monitoreoIa.caso.cerradoAlInstante", {
                        defaultValue: "caso cerrado al instante",
                      })
                    : t("monitoreoIa.caso.atendidoEn", {
                        defaultValue: "caso atendido en {{lapso}}",
                        lapso: lapso(caseView.duracionMin, t),
                      })}
            </span>
            {/* El lugar se declara ausente en vez de omitirse: «no se supo
                dónde» es en sí mismo parte del caso cuando lo que falló fue
                justamente la señal. */}
            <span className="flex min-w-0 items-center gap-1">
              <i className="ri-map-pin-line shrink-0" aria-hidden="true" />
              <span className="truncate">
                {caseView.donde ??
                  t("monitoreoIa.caso.sinLugar", { defaultValue: "sin ubicación" })}
              </span>
            </span>
          </div>

          {caseView.porQue ? (
            <p className="mt-1.5 border-t border-line pt-1.5 text-[10.5px] leading-relaxed text-content-secondary">
              {caseView.porQue}
            </p>
          ) : null}
        </div>

        {caseView.isOpen ? (
          cerrando ? (
            <div className="rounded-lg border border-warning/40 bg-warning/5 p-2">
              <label className="block text-[10.5px] text-content-secondary">
                {t("monitoreoIa.cerrarCaso.porQue", {
                  defaultValue: "¿Por qué se cierra? Queda guardado con tu nombre.",
                })}
              </label>
              <textarea
                rows={2}
                value={nota}
                onChange={(e) => setNota(e.target.value)}
                autoFocus
                placeholder={t("monitoreoIa.cerrarCaso.ejemplo", {
                  defaultValue: "Hablé con el operador por radio, la unidad está bien.",
                })}
                className="mt-1 w-full resize-none rounded-md border border-outline/40 bg-surface px-2 py-1.5 text-[11px] text-content placeholder:text-content-faint"
              />
              {cerrar.isError ? (
                <p className="mt-1 text-[10px] max-sm:text-[11px] text-error">
                  {t("monitoreoIa.cerrarCaso.fallo", {
                    defaultValue: "No se pudo cerrar. El caso sigue abierto.",
                  })}
                </p>
              ) : null}
              <div className="mt-1.5 flex justify-end gap-1.5">
                <button
                  type="button"
                  onClick={() => { setCerrando(false); setNota(""); }}
                  className="rounded-md px-2 py-1 text-[10.5px] text-content-faint hover:text-content"
                >
                  {t("general.cancel", { defaultValue: "Cancelar" })}
                </button>
                <button
                  type="button"
                  // Tres caracteres es el mínimo del backend. Deshabilitar en
                  // vez de dejar mandar y fallar evita el viaje de ida y vuelta
                  // para decir algo que ya se sabe aquí.
                  disabled={nota.trim().length < 3 || cerrar.isPending}
                  onClick={() =>
                    cerrar.mutate(
                      { caseId: caseView.id, resolution: nota.trim() },
                      { onSuccess: () => { setCerrando(false); setNota(""); } },
                    )
                  }
                  className="rounded-md bg-warning/20 px-2.5 py-1 text-[10.5px] font-medium text-warning transition-colors hover:bg-warning/30 disabled:opacity-40"
                >
                  {cerrar.isPending
                    ? t("monitoreoIa.cerrarCaso.cerrando", { defaultValue: "Cerrando…" })
                    : t("monitoreoIa.cerrarCaso.confirmar", { defaultValue: "Cerrar caso" })}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setCerrando(true)}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-outline/40 py-1.5 text-[11px] text-content-secondary transition-colors hover:bg-subtle"
            >
              <i className="ri-check-double-line" aria-hidden="true" />
              {t("monitoreoIa.cerrarCaso.boton", { defaultValue: "Cerrar a mano" })}
            </button>
          )
        ) : null}

        {caseView && caseView.theftScore > 0 ? (
          <div>
            <div className="mb-1 flex items-center justify-between font-mono text-[9.5px] uppercase tracking-wider text-content-muted">
              <span>{t("monitoreoIa.monitoringCenter.theftScore")}</span>
              <span className="tabular-nums text-content-secondary">
                {caseView.theftScore}
              </span>
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-subtle">
              <div
                className="h-full rounded-full bg-error"
                style={{ width: `${Math.min(caseView.theftScore, 100)}%` }}
              />
            </div>
          </div>
        ) : null}

        {row?.trip ? (
          <div className="rounded-lg border border-line bg-surface p-2">
            <p className="font-mono text-[9.5px] uppercase tracking-wider text-content-muted">
              {t("monitoreoIa.monitoringCenter.trip")}
            </p>
            <p className="mt-0.5 text-[11px] text-content">
              {row?.trip?.name ?? t("monitoreoIa.trips.untitled")}
            </p>
            {row?.trip?.status ? (
              <p className="text-[10px] max-sm:text-[11px] text-content-faint">{row.trip.status}</p>
            ) : null}
          </div>
        ) : null}

        {caseView ? (
          <div className="space-y-2 border-t border-line pt-3">
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge tone={CASE_URGENCY_TONES[caseView.urgency]} size="sm">
                {t(`monitoreoIa.caseEvents.urgency.${caseView.urgency}`)}
              </Badge>
              <span className="text-[11.5px] font-semibold text-content">
                {labelOfCase(caseView)}
              </span>
              {!caseView.isOpen ? (
                <Badge tone="success" size="sm">
                  {t("monitoreoIa.caseEvents.status.resuelto")}
                </Badge>
              ) : null}
              {caseView.requiresHuman ? (
                <Badge tone="error" size="sm">
                  <i className="ri-user-follow-line text-[9px] max-sm:text-[10px]" />
                  {t("monitoreoIa.caseEvents.requiresHuman")}
                </Badge>
              ) : null}
            </div>

            {caseView.summary ? (
              <p className="text-[10.5px] leading-snug text-content-secondary">
                {caseView.summary}
              </p>
            ) : null}

            {caseView.resolution ? (
              <p className="rounded-lg border border-success/30 bg-success/10 p-2 text-[10.5px] leading-snug text-success">
                {caseView.resolution}
              </p>
            ) : null}

            <p className="font-mono text-[9.5px] uppercase tracking-wider text-content-faint">
              {formatAgo(caseView.openedAt, t)}
              {caseView.callsCount > 0
                ? ` · ${t("monitoreoIa.caseEvents.callsCount", { count: caseView.callsCount })}`
                : ""}
            </p>

            <CaseLadder
              step={caseView.step}
              attempts={caseView.attempts}
              maxAttempts={maxAttempts}
            />
          </div>
        ) : null}

        <CaseDashcamShots
          media={mediaQuery.data?.media ?? []}
          isLoading={mediaQuery.isLoading}
        />

        {eventsQuery.isLoading ? (
          <PanelPending label={t("monitoreoIa.monitoringCenter.loadingLadder")} />
        ) : null}

        {steps.length > 0 ? (
          <ol className="space-y-2 border-t border-line pt-3">
            {steps.map((step) => (
              <li key={`${step.step_index}-${step.executed_at}`} className="flex items-start gap-2">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-subtle">
                  <i
                    className={`${CASE_STEP_CHANNEL_ICONS[step.channel] ?? "ri-circle-line"} text-[9px] max-sm:text-[10px] text-content-secondary`}
                  />
                </span>
                <div className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[10.5px] font-semibold text-content">{step.label}</span>
                    <Badge tone={CASE_STEP_STATUS_TONES[step.status] ?? "neutral"} size="sm">
                      {t(`monitoreoIa.caseEvents.stepStatus.${step.status}`, {
                        defaultValue: step.status,
                      })}
                    </Badge>
                  </span>
                  <span className="mt-0.5 block text-[9.5px] max-sm:text-[10px] text-content-faint">
                    {when(step.executed_at)}
                    {step.recipient_name ? ` · ${step.recipient_name}` : ""}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        ) : null}

        {lastCall ? (
          <LastCallAudio call={lastCall} tone={tone} />
        ) : callsQuery.isLoading ? (
          <PanelPending label={t("monitoreoIa.monitoringCenter.loadingCalls")} />
        ) : (
          <p className="border-t border-line pt-3 text-[10.5px] text-content-faint">
            {t("monitoreoIa.monitoringCenter.noCalls")}
          </p>
        )}
      </div>
    </div>
  );
}
